import {createContext, useContext} from "react";
import {space} from "../../provider_api";
import {EditableProps} from "./Editable";
import IErrorProto = space.kenko.proto.IErrorProto;

/**
 * The editable context contains information on how to set the value in
 * the enclosing data type;
 */
export type EditableContext<EnclosingType extends object> = {
  mode: 'create' | 'update',
  editing: boolean,
  workingCopy: EnclosingType,
  setWorkingCopy: (workingCopy: EnclosingType) => void;

  // Field-by-field errors as a result of attempting to save.
  errors: IErrorProto[];
};

export const EditableContext = createContext({editing: false} as EditableContext<any>);

/**
 * This presents a bit of an easier interface than using the context directly. It means the editors
 * don't have to care about the wrapper type.
 */
export const useEditable = <EnclosingType extends object, V extends any>(props: EditableProps<EnclosingType, V>) => {

  const {
    workingCopy,
    setWorkingCopy,
    editing,
    errors,
    mode
  } = useContext(EditableContext) as EditableContext<EnclosingType>;

  const getValue = (): V | null | undefined => {

    if (props.getter) {
      return props.getter(workingCopy);
    }

    if (props.field) {
      return workingCopy[props.field] as V;
    }

    throw new Error('Either `getter` or `field` must be set');
  }

  const setValue = (newValue: V) => {

    if (props.setter) {
      console.log('setting working copy', newValue)
      setWorkingCopy(props.setter(workingCopy, newValue));
      return;
    }

    if (props.field) {
      setWorkingCopy({
        ...workingCopy,
        [props.field as string]: newValue
      });
      return;
    }
    
    throw new Error('Either `setter` or `field` must be set');
  }

  return {
    value: getValue(),
    setValue,
    editing,
    errors,
    mode,
  }
}